import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  Input,
  InputNumber,
  Icon,
  message,
  Slider,
  Select,
  Tooltip,
  Button,
  Progress,
  Spin
} from 'antd'
import { config } from '../../../../../constants'
import AudioHistoryModal from './AudioHistoryModal'
import './VoiceoverModal.scss'

const { TextArea } = Input
const { Option } = Select
const AUDIO_PREVIEW_BASE_URL = `${config.videoGenerationUrl.replace(
  /\/?$/,
  '/'
)}audio/`

const EMPTY_VOICE_DESCRIPTION = {
  age: '',
  accent: '',
  emotion: '',
  pace: '',
  additional_traits: ''
}

const EMOTION_OPTIONS = [
  { value: 'calm', label: 'Calm' },
  { value: 'warm', label: 'Warm' },
  { value: 'excited', label: 'Excited' },
  { value: 'somber', label: 'Somber' },
  { value: 'tense', label: 'Tense' }
]

const PACE_OPTIONS = [
  { value: 'slow', label: 'Slow' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'fast', label: 'Fast' }
]

const toCleanString = value => {
  if (value == null) return ''
  return String(value).trim()
}

const formatGenerationStatus = value => {
  const status = toCleanString(value).replace(/[_-]+/g, ' ')
  if (!status) return ''
  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`
}

const buildVoiceDescriptionPayload = voiceDescription =>
  Object.keys(EMPTY_VOICE_DESCRIPTION).reduce((payload, key) => {
    const value = toCleanString(voiceDescription[key])
    if (value) {
      payload[key] = value
    }
    return payload
  }, {})

const getVoicePreviewUrl = voice =>
  voice?.audio_url || voice?.audioUrl || voice?.preview_url || ''

const resolvePreviewSource = (previewUrl, voiceId) => {
  if (typeof previewUrl === 'string' && previewUrl.trim()) {
    const trimmed = previewUrl.trim()

    if (
      /^https?:\/\//i.test(trimmed) ||
      trimmed.startsWith('blob:') ||
      trimmed.startsWith('data:')
    ) {
      return trimmed
    }

    if (trimmed.startsWith('/')) {
      return `${config.videoGenerationUrl.replace(/\/?$/, '')}${trimmed}`
    }

    return trimmed
  }

  if (!voiceId) return ''
  return `${AUDIO_PREVIEW_BASE_URL}${voiceId}`
}

const getModeLabel = isEasyAudioMode =>
  isEasyAudioMode ? 'Narration' : 'Dialogue'

const clampNumber = (value, min, max) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return min
  return Math.min(max, Math.max(min, parsed))
}

const CurrentAudioPlayer = ({ src, audioRef, onPlay }) => {
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.pause()
    audio.currentTime = 0
    audio.load()
  }, [src, audioRef])

  if (!src) {
    return (
      <div className="voiceover-native-audio voiceover-native-audio--empty">
        <div className="voiceover-native-audio__empty-copy">
          Generated audio will appear here after you run voiceover creation.
        </div>
      </div>
    )
  }

  return (
    <div className="voiceover-native-audio">
      <audio
        key={src}
        ref={audioRef}
        controls
        preload="metadata"
        className="voiceover-native-audio__player"
        onPlay={onPlay}
      >
        <source src={src} />
        Your browser does not support the audio element.
      </audio>
    </div>
  )
}

const SettingControl = ({
  label,
  tip,
  value,
  onChange,
  min,
  max,
  step,
  marks
}) => {
  const handleNumberChange = nextValue => {
    if (nextValue == null || nextValue === '') return
    onChange(clampNumber(nextValue, min, max))
  }

  return (
    <div className="voiceover-setting">
      <div className="voiceover-setting__head">
        <div className="voiceover-setting__label">
          <span>{label}</span>
          <Tooltip title={tip}>
            <Icon type="question-circle" />
          </Tooltip>
        </div>
        <InputNumber
          value={value}
          min={min}
          max={max}
          step={step}
          precision={step < 1 ? 1 : 0}
          onChange={handleNumberChange}
          className="voiceover-setting__input"
        />
      </div>
      <Slider
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
        marks={marks}
        tipFormatter={null}
      />
    </div>
  )
}

const VoiceoverModal = ({
  visible,
  scene,
  voices = [],
  selectedVoice: globalSelectedVoice,
  onClose,
  formData,
  onGenerate,
  onNavigateToStep,
  isClusteringAudio,
  audioClusteringProgress = 0,
  audioClusteringStatus = '',
  audioMode,
  isSceneAudioGenerating = false,
  onStopGeneration,
  onRestoreAudio,
  onDeleteAudioHistory,
  openHistoryOnOpen = false
}) => {
  const [voiceText, setVoiceText] = useState('')
  const [speedFactor, setSpeedFactor] = useState(0.7)
  const [exaggeration, setExaggeration] = useState(0.5)
  const [cfgWeight, setCfgWeight] = useState(0.8)
  const [customPrompt, setCustomPrompt] = useState('')
  const [voiceDescription, setVoiceDescription] = useState({
    ...EMPTY_VOICE_DESCRIPTION
  })
  const [isGenerating, setIsGenerating] = useState(false)
  const [showStopConfirm, setShowStopConfirm] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)
  const [previewPlaying, setPreviewPlaying] = useState(null)
  const previewRef = useRef(null)
  const currentAudioRef = useRef(null)

  const sceneKey = scene?.id
  const sceneVoiceoverData =
    scene?.aggregated_voiceover && typeof scene.aggregated_voiceover === 'object'
      ? scene.aggregated_voiceover
      : null
  const isEasyAudioMode = audioMode === 'narration'
  const isAudioGenerating = Boolean(isGenerating || isSceneAudioGenerating)
  const hasAudio = Boolean(scene?.audio_link)
  const audioHistory = Array.isArray(scene?.audio_history)
    ? scene.audio_history.filter(item => item?.url)
    : []
  const charCount = voiceText?.length || 0
  const overLimit = charCount > 5000
  const voiceDescriptionPayload =
    buildVoiceDescriptionPayload(voiceDescription)
  const hasVoiceDescription = Object.keys(voiceDescriptionPayload).length > 0
  const canGenerateVoiceover =
    Boolean(voiceText && voiceText.trim()) &&
    !overLimit &&
    (Boolean(globalSelectedVoice) || hasVoiceDescription)
  const generationProgress = clampNumber(
    sceneVoiceoverData?.progress || audioClusteringProgress,
    0,
    100
  )
  const generationStatusState = toCleanString(
    sceneVoiceoverData?.status || audioClusteringStatus
  ).toLowerCase()
  const formattedGenerationStatus = formatGenerationStatus(
    sceneVoiceoverData?.status || audioClusteringStatus
  )
  const generationStatusText =
    formattedGenerationStatus ||
    (isEasyAudioMode
      ? 'Generating voiceover audio...'
      : 'Generating dialogue tracks...')
  const generationTitle = isEasyAudioMode
    ? 'Generating voiceover'
    : 'Generating dialogue audio'

  useEffect(() => {
    if (visible && scene) {
      const draft = scene.aggregated_voiceover
      setVoiceText(typeof draft === 'string' ? draft : draft?.text || '')
    }
  }, [visible, scene?.id])

  useEffect(() => {
    return () => {
      if (previewRef.current) {
        previewRef.current.pause()
      }
      if (currentAudioRef.current) {
        currentAudioRef.current.pause()
      }
    }
  }, [])

  useEffect(() => {
    if (!visible) {
      setShowHistoryModal(false)
      setShowAdvancedSettings(false)
      if (previewRef.current) {
        previewRef.current.pause()
        previewRef.current = null
      }
      if (currentAudioRef.current) {
        currentAudioRef.current.pause()
      }
      setPreviewPlaying(null)
    }
  }, [visible])

  useEffect(() => {
    if (visible && openHistoryOnOpen) {
      setShowHistoryModal(true)
    }
  }, [visible, openHistoryOnOpen])

  const handleGenerate = async () => {
    const nextVoiceDescriptionPayload =
      buildVoiceDescriptionPayload(voiceDescription)

    if (
      !globalSelectedVoice &&
      !Object.keys(nextVoiceDescriptionPayload).length
    ) {
      message.warning('Select a voice or describe one first')
      return
    }

    if (!(voiceText && voiceText.trim())) {
      message.warning('Enter voiceover text')
      return
    }

    setIsGenerating(true)
    try {
      await onGenerate({
        voice_id: globalSelectedVoice || undefined,
        text: voiceText,
        scene_structure: formData?.shotList,
        scene_key: sceneKey,
        speed_factor: speedFactor,
        exaggeration,
        cfg_weight: cfgWeight,
        prompt: customPrompt,
        language: formData?.language || 'en',
        ...nextVoiceDescriptionPayload
      })
      message.success('Voiceover generated')
    } catch (e) {
      if (e?.message !== 'Audio generation stopped') {
        message.error(e?.message || 'Generation failed')
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const handleConfirmStopGeneration = () => {
    if (onStopGeneration) {
      onStopGeneration()
    }
    setIsGenerating(false)
    setShowStopConfirm(false)
  }

  const togglePreview = id => {
    if (previewPlaying === id) {
      if (previewRef.current) {
        previewRef.current.pause()
      }
      previewRef.current = null
      setPreviewPlaying(null)
      return
    }

    if (previewRef.current) {
      previewRef.current.pause()
    }
    if (currentAudioRef.current) {
      currentAudioRef.current.pause()
    }

    const voice = voices.find(
      v => v?.id === id || v?.voice_id === id || String(v) === String(id)
    )
    const sourceUrl = resolvePreviewSource(getVoicePreviewUrl(voice), id)

    if (!sourceUrl) {
      message.error('Preview failed')
      return
    }

    const audio = new Audio(sourceUrl)
    audio.play().catch(() => {
      if (previewRef.current === audio) {
        previewRef.current = null
        setPreviewPlaying(null)
      }
      message.error('Preview failed')
    })
    audio.onended = () => {
      if (previewRef.current === audio) {
        previewRef.current = null
        setPreviewPlaying(null)
      }
    }
    previewRef.current = audio
    setPreviewPlaying(id)
  }

  const handleCurrentAudioPlay = () => {
    if (previewRef.current) {
      previewRef.current.pause()
      previewRef.current = null
    }
    setPreviewPlaying(null)
  }

  const voiceName = id => {
    if (!id) return null
    const voice = voices.find(
      v => v?.id === id || v?.voice_id === id || v === id
    )
    return voice?.name || voice?.label || id
  }

  const handleRestoreAudio = audioVersion => {
    if (!onRestoreAudio) return
    onRestoreAudio(audioVersion)
    setShowHistoryModal(false)
  }

  const updateVoiceDescription = (key, value) => {
    setVoiceDescription(current => ({
      ...current,
      [key]: value || ''
    }))
  }

  const clearVoiceDescription = () => {
    setVoiceDescription({ ...EMPTY_VOICE_DESCRIPTION })
  }

  const advancedControls = [
    {
      key: 'speed',
      label: 'Speed',
      tip: '0.5x = half speed, 2.0x = double speed',
      value: speedFactor,
      setValue: value => setSpeedFactor(clampNumber(value, 0.5, 2.0)),
      min: 0.5,
      max: 2.0,
      step: 0.1,
      marks: { 0.5: '0.5', 1.0: '1.0', 2.0: '2.0' }
    },
    {
      key: 'exaggeration',
      label: 'Exaggeration',
      tip: 'Controls expression intensity',
      value: exaggeration,
      setValue: value => setExaggeration(clampNumber(value, 0.1, 2.0)),
      min: 0.1,
      max: 2.0,
      step: 0.1,
      marks: { 0.1: '0.1', 1.0: '1.0', 2.0: '2.0' }
    },
    {
      key: 'cfg',
      label: 'CFG Weight',
      tip: 'Controls how closely audio follows your script',
      value: cfgWeight,
      setValue: value => setCfgWeight(clampNumber(value, 0.0, 1.0)),
      min: 0.0,
      max: 1.0,
      step: 0.1,
      marks: { 0.0: '0.0', 0.5: '0.5', 1.0: '1.0' }
    }
  ]

  if (isClusteringAudio) {
    return (
      <>
        <Modal
          visible={visible}
          footer={null}
          width={460}
          maskClosable
          onCancel={onClose}
          centered
          bodyStyle={{ padding: 0 }}
          wrapClassName="voiceover-modal-progress-wrap"
        >
          <div className="voiceover-progress">
            <div className="voiceover-progress__spinner">
              <Spin size="large" />
            </div>
            <h3 className="voiceover-progress__title">Generating Audio</h3>
            <p className="voiceover-progress__text">
              {isEasyAudioMode
                ? 'Building your narration track.'
                : 'Rebuilding narration and dialogue tracks.'}
            </p>
            <Progress
              percent={audioClusteringProgress}
              status={
                generationStatusState === 'failed' ? 'exception' : 'active'
              }
            />
            {generationStatusText && (
              <div className="voiceover-progress__status">
                {generationStatusText}
              </div>
            )}
            <Button
              className="voiceover-progress__stop-btn"
              onClick={() => setShowStopConfirm(true)}
            >
              Stop Generation
            </Button>
          </div>
        </Modal>

        <Modal
          maskClosable={false}
          visible={showStopConfirm}
          onCancel={() => setShowStopConfirm(false)}
          footer={null}
          width={420}
          centered
          bodyStyle={{ padding: 0 }}
          wrapClassName="voiceover-modal-stop-wrap"
        >
          <div className="voiceover-stop-confirm">
            <div className="voiceover-stop-confirm__icon">
              <Icon type="exclamation-circle" />
            </div>
            <h3 className="voiceover-stop-confirm__title">
              Stop audio generation?
            </h3>
            <p className="voiceover-stop-confirm__text">
              This generation request will be closed immediately. The request
              cost will still apply.
            </p>
            <div className="voiceover-stop-confirm__actions">
              <Button onClick={() => setShowStopConfirm(false)}>
                Continue
              </Button>
              <Button
                type="primary"
                className="voiceover-stop-confirm__danger-btn"
                onClick={() => {
                  handleConfirmStopGeneration()
                  onClose()
                }}
              >
                Stop
              </Button>
            </div>
          </div>
        </Modal>
      </>
    )
  }

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={1080}
        maskClosable
        destroyOnClose
        centered
        bodyStyle={{ padding: 0 }}
        wrapClassName="voiceover-modal-wrap"
      >
        <div className="voiceover-modal">
          <div className="voiceover-modal__shell">
            <header className="voiceover-modal__header">
              <div className="voiceover-modal__header-main">
                <div className="voiceover-modal__header-icon">
                  <Icon type="sound" />
                </div>
                <div className="voiceover-modal__header-copy">
                  <h2>Voiceover Studio</h2>
                  <p>
                    {scene?.title || 'Scene'} · {getModeLabel(isEasyAudioMode)}
                  </p>
                </div>
              </div>

              {hasAudio && audioHistory.length > 0 && (
                <Button
                  className="voiceover-modal__history-btn"
                  onClick={() => setShowHistoryModal(true)}
                >
                  <Icon type="history" />
                  History ({audioHistory.length})
                </Button>
              )}
            </header>

            {isAudioGenerating && (
              <section
                className="voiceover-generation-status"
                role="status"
                aria-live="polite"
              >
                <div className="voiceover-generation-status__main">
                  <div className="voiceover-generation-status__icon">
                    <Icon type="loading" spin />
                  </div>
                  <div className="voiceover-generation-status__copy">
                    <h3>{generationTitle}</h3>
                    <p>{generationStatusText}</p>
                  </div>
                </div>
                <div className="voiceover-generation-status__progress">
                  <div className="voiceover-generation-status__meta">
                    <span>Audio render in progress</span>
                    <strong>{generationProgress}%</strong>
                  </div>
                  <Progress
                    percent={generationProgress}
                    status={
                      generationStatusState === 'failed'
                        ? 'exception'
                        : 'active'
                    }
                    showInfo={false}
                  />
                </div>
              </section>
            )}

            <div className="voiceover-modal__layout">
              <div className="voiceover-modal__content-column">
                <section className="voiceover-panel voiceover-panel--footer voiceover-panel--current-audio">
                  <div className="voiceover-panel__head voiceover-panel__head--footer">
                    <div>
                      <h3>Current audio</h3>
                    </div>
                  </div>

                  <CurrentAudioPlayer
                    src={scene?.audio_link}
                    audioRef={currentAudioRef}
                    onPlay={handleCurrentAudioPlay}
                  />
                </section>

                <section className="voiceover-panel voiceover-panel--editor">
                  <div className="voiceover-panel__head">
                    <div>
                      <h3>Narration text</h3>
                      <p>Use the approved script for this scene voiceover.</p>
                    </div>
                  </div>

                  <div className="voiceover-editor">
                    <TextArea
                      rows={5}
                      value={voiceText}
                      onChange={e => setVoiceText(e.target.value)}
                      placeholder="Enter the voiceover script..."
                      disabled={isAudioGenerating}
                      className="voiceover-editor__textarea"
                    />
                    <div
                      className={`voiceover-editor__counter ${overLimit ? 'voiceover-editor__counter--error' : ''
                        }`}
                    >
                      {charCount.toLocaleString()} / 5,000
                    </div>
                  </div>

                  {overLimit && (
                    <div className="voiceover-panel__error">
                      Exceeds 5,000 character limit.
                    </div>
                  )}
                </section>

                <section className="voiceover-panel voiceover-panel--prompt">
                  <div className="voiceover-panel__head">
                    <div>
                      <h3>Custom prompt</h3>
                      <p>
                        Add optional direction for tone, pacing, pronunciation,
                        or delivery style.
                      </p>
                    </div>
                  </div>

                  <div className="voiceover-editor voiceover-editor--prompt">
                    <TextArea
                      rows={2}
                      value={customPrompt}
                      onChange={e => setCustomPrompt(e.target.value)}
                      placeholder="Add extra instructions for voice tone, emotion, pacing, or style..."
                      disabled={isAudioGenerating}
                      className="voiceover-editor__textarea voiceover-editor__textarea--prompt"
                    />
                  </div>
                </section>
              </div>

              <aside className="voiceover-modal__sidebar">
                <section className="voiceover-panel voiceover-panel--sidebar">
                  <div className="voiceover-panel__head">
                    <div>
                      <h3>Configuration</h3>
                      <p>
                        Review your voice and tune generation.
                      </p>
                    </div>
                  </div>

                  {!globalSelectedVoice ? (
                    <section className="voiceover-modal__alert voiceover-modal__alert--warning">
                      <div className="voiceover-modal__alert-icon">
                        <Icon type="exclamation-circle" />
                      </div>
                      <div className="voiceover-modal__alert-copy">
                        <h3>No voice selected</h3>
                        <p>
                          Describe a voice below, or choose a saved voice from
                          the Voiceover step.
                        </p>
                      </div>
                      <Button
                        className="voiceover-modal__secondary-btn"
                        onClick={() => {
                          onClose()
                          if (onNavigateToStep) {
                            onNavigateToStep('voiceover')
                          }
                        }}
                      >
                        Select voice
                      </Button>
                    </section>
                  ) : (
                    <div className="voiceover-voice-card">
                      <div className="voiceover-voice-card__copy">
                        <span className="voiceover-voice-card__eyebrow">
                          Voice picker
                        </span>
                        <strong>{voiceName(globalSelectedVoice)}</strong>
                      </div>
                      <button
                        type="button"
                        className="voiceover-voice-card__preview"
                        onClick={() => togglePreview(globalSelectedVoice)}
                      >
                        <Icon
                          type={
                            previewPlaying === globalSelectedVoice
                              ? 'pause-circle'
                              : 'play-circle'
                          }
                        />
                        <span>
                          {previewPlaying === globalSelectedVoice
                            ? 'Pause'
                            : 'Play'}
                        </span>
                      </button>
                    </div>
                  )}

                  <section
                    className="voiceover-profile"
                    aria-labelledby="voiceover-profile-title"
                  >
                    <div className="voiceover-profile__head">
                      <div className="voiceover-profile__title">
                        <h4 id="voiceover-profile-title">
                          Voice description
                        </h4>
                        <p>
                          Optional cues for a new voice or subtle direction for
                          the selected one.
                        </p>
                      </div>
                      {hasVoiceDescription && (
                        <button
                          type="button"
                          className="voiceover-profile__clear"
                          onClick={clearVoiceDescription}
                          disabled={isAudioGenerating}
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <div className="voiceover-profile__grid">
                      <label className="voiceover-profile__field">
                        <span>Age</span>
                        <Input
                          value={voiceDescription.age}
                          placeholder="35 or middle-aged"
                          onChange={e =>
                            updateVoiceDescription('age', e.target.value)
                          }
                          disabled={isAudioGenerating}
                        />
                      </label>

                      <label className="voiceover-profile__field">
                        <span>Accent</span>
                        <Input
                          value={voiceDescription.accent}
                          placeholder="British, neutral..."
                          onChange={e =>
                            updateVoiceDescription('accent', e.target.value)
                          }
                          disabled={isAudioGenerating}
                        />
                      </label>

                      <label className="voiceover-profile__field">
                        <span>Emotion</span>
                        <Select
                          allowClear
                          value={voiceDescription.emotion || undefined}
                          placeholder="Select"
                          onChange={value =>
                            updateVoiceDescription('emotion', value)
                          }
                          disabled={isAudioGenerating}
                          className="voiceover-profile__select"
                        >
                          {EMOTION_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </label>

                      <label className="voiceover-profile__field">
                        <span>Pace</span>
                        <Select
                          allowClear
                          value={voiceDescription.pace || undefined}
                          placeholder="Select"
                          onChange={value =>
                            updateVoiceDescription('pace', value)
                          }
                          disabled={isAudioGenerating}
                          className="voiceover-profile__select"
                        >
                          {PACE_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </label>

                      <label className="voiceover-profile__field voiceover-profile__field--wide">
                        <span>Additional traits</span>
                        <TextArea
                          rows={3}
                          value={voiceDescription.additional_traits}
                          placeholder="Gravelly, radio-announcer quality"
                          onChange={e =>
                            updateVoiceDescription(
                              'additional_traits',
                              e.target.value
                            )
                          }
                          disabled={isAudioGenerating}
                          className="voiceover-profile__traits"
                        />
                      </label>
                    </div>
                  </section>

                  <section className="voiceover-settings">
                    <button
                      type="button"
                      className="voiceover-settings__toggle"
                      onClick={() =>
                        setShowAdvancedSettings(current => !current)
                      }
                      aria-expanded={showAdvancedSettings}
                    >
                      <div className="voiceover-settings__toggle-copy">
                        <h4>Advanced settings</h4>
                        <p>
                          Adjust delivery and prompt adherence only if needed.
                        </p>
                      </div>
                      <span className="voiceover-settings__toggle-icon">
                        <Icon type={showAdvancedSettings ? 'up' : 'down'} />
                      </span>
                    </button>

                    {showAdvancedSettings && (
                      <div className="voiceover-settings__body">
                        {advancedControls.map(control => (
                          <SettingControl
                            key={control.key}
                            label={control.label}
                            tip={control.tip}
                            value={control.value}
                            onChange={control.setValue}
                            min={control.min}
                            max={control.max}
                            step={control.step}
                            marks={control.marks}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                </section>
              </aside>
            </div>
          </div>

          <div className="voiceover-modal__footer">
            <div className="voiceover-modal__footer-actions">
              <Button onClick={onClose}>
                {isAudioGenerating ? 'Close' : 'Cancel'}
              </Button>
              {isAudioGenerating && (
                <Button onClick={() => setShowStopConfirm(true)}>
                  Stop Generation
                </Button>
              )}
              <Button
                type="primary"
                onClick={handleGenerate}
                loading={isAudioGenerating}
                disabled={!canGenerateVoiceover || isAudioGenerating}
                className="voiceover-modal__primary-btn"
              >
                {isAudioGenerating
                  ? 'Generating Voiceover'
                  : 'Generate Voiceover'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        maskClosable={false}
        visible={showStopConfirm}
        onCancel={() => setShowStopConfirm(false)}
        footer={null}
        width={420}
        centered
        bodyStyle={{ padding: 0 }}
        wrapClassName="voiceover-modal-stop-wrap"
      >
        <div className="voiceover-stop-confirm">
          <div className="voiceover-stop-confirm__icon">
            <Icon type="exclamation-circle" />
          </div>
          <h3 className="voiceover-stop-confirm__title">
            Stop audio generation?
          </h3>
          <p className="voiceover-stop-confirm__text">
            This generation request will be closed immediately. The request
            cost will still apply.
          </p>
          <div className="voiceover-stop-confirm__actions">
            <Button onClick={() => setShowStopConfirm(false)}>Continue</Button>
            <Button
              type="primary"
              className="voiceover-stop-confirm__danger-btn"
              onClick={handleConfirmStopGeneration}
            >
              Stop
            </Button>
          </div>
        </div>
      </Modal>

      <AudioHistoryModal
        visible={showHistoryModal}
        history={audioHistory}
        currentAudioUrl={scene?.audio_link}
        onCancel={() => setShowHistoryModal(false)}
        onSelect={handleRestoreAudio}
        onDelete={onDeleteAudioHistory}
      />
    </>
  )
}

export default VoiceoverModal
